import { useEffect, useState } from "react";
import { Inbox } from "lucide-react";
import { db } from "@/lib/db";
import { formatRelativeDate, formatBytes } from "@/lib/formatters";
import { FileTypeIcon } from "@/components/FileTypeIcon";
import type { Document, Occurrence } from "@/types";

interface ArrivalItem {
  document: Document;
  occurrence: Occurrence;
}

export default function Arrivals() {
  const [items, setItems] = useState<ArrivalItem[]>([]);

  useEffect(() => {
    db.occurrences.orderBy("receivedAt").reverse().limit(50).toArray().then(async (occs) => {
      const docs = await db.documents.bulkGet(occs.map((o) => o.documentId));
      const result: ArrivalItem[] = [];
      for (let i = 0; i < occs.length; i++) {
        if (docs[i]) result.push({ document: docs[i]!, occurrence: occs[i] });
      }
      setItems(result);
    });
  }, []);

  return (
    <div className="space-y-5">
      <h3 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">Recent Arrivals ({items.length})</h3>

      {items.length === 0 ? (
        <div className="rounded-xl border border-border-default bg-surface-0 p-8 text-center shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-surface-2 dark:bg-dark-surface-2 mb-3">
            <Inbox size={24} className="text-text-muted" />
          </div>
          <p className="text-sm font-medium text-text-secondary dark:text-dark-text-secondary">No recent arrivals</p>
          <p className="mt-1 text-xs text-text-muted">Sync your email to see new attachments</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.occurrence.id}
              className="flex items-center gap-3 rounded-xl border border-border-default bg-surface-0 p-3 shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1"
            >
              <FileTypeIcon extension={item.document.extension} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-text-primary dark:text-dark-text-primary">
                  {item.document.canonicalName}
                </p>
                <p className="text-xs text-text-muted dark:text-dark-text-muted">
                  {item.occurrence.senderEmail} · {item.occurrence.subject}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-text-muted dark:text-dark-text-muted">{formatBytes(item.document.sizeBytes)}</p>
                <p className="text-xs text-text-muted">{formatRelativeDate(item.occurrence.receivedAt)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
